import React, { useState } from 'react';
import { parseDiff, Diff, Hunk } from 'react-diff-view';
import { Icon } from '../Icon';
import 'react-diff-view/style/index.css';
import 'prismjs/themes/prism.css';
import 'prism-color-variables/variables.css';
import 'prism-color-variables/themes/duracula.css';

interface DiffViewProps {
    diff: string;
}

/** Stable DOM id for a file path in a DiffView (used as scroll target). */
export function diffFileDomId(path: string): string {
    return `diff-file-${encodeURIComponent(path)}`;
}

function fileKey(file: { oldPath?: string; newPath?: string }, index: number): string {
    return file.newPath || file.oldPath || `file-${index}`;
}

function DiffView(props: DiffViewProps) {
    const { diff } = props;
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

    const files = React.useMemo(() => parseDiff(diff), [diff]);

    const toggle = (key: string) => {
        setCollapsed((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const renderFile = (file: any, index: number) => {
        const { oldPath, newPath, type, hunks } = file;
        const key = fileKey(file, index);
        const path = newPath || oldPath || key;
        const isCollapsed = !!collapsed[key];

        return (
            <div
                key={key}
                id={diffFileDomId(path)}
                className="text-xs border mt-2 p-0 scroll-mt-2"
            >
                <div
                    className="text-sm p-2 border-b font-bold flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800"
                    onClick={() => toggle(key)}
                >
                    <Icon type={isCollapsed ? 'right' : 'down'} className="inline" />
                    &nbsp;
                    {path}
                </div>
                {!isCollapsed && (
                    <Diff viewType="split" diffType={type} hunks={hunks} optimizeSelection>
                        {(fileHunks) => fileHunks.map((hunk) => (
                            <Hunk key={hunk.content} hunk={hunk} />
                        ))}
                    </Diff>
                )}
            </div>
        );
    };

    return (
        <div>
            {files.map(renderFile)}
        </div>
    );
}

export default DiffView;
